import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminShippingApi, adminKolayGelsinApi, adminTaxApi } from '../api/operations';
import { shippingWebhookApi, type TrackingEventInput } from '../api/sales';
import type {
  CarrierUpsertDto,
  ShipmentTrackingStatus,
  ShippingRateType,
  ShippingRateUpsertDto,
  ShippingSpeed,
  ShippingZoneScope,
  ShippingZoneUpsertDto,
  KolayGelsinSettingsUpsertDto,
  KolayGelsinTrackingResultDto,
  TaxClassUpsertDto,
} from '../api/types';
import {
  shipmentTrackingStatusValues,
  shippingRateTypeValues,
  shippingSpeedValues,
  shippingZoneScopeValues,
} from '../api/types';
import { PageHeader } from '../components/Shell';
import { ConfirmDialog, Field, FormErrors, Modal, PageLoader, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { SearchableSelect } from '../components/SearchableSelect';
import { adminShippingLocationsApi } from '../api/shippingLocations';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, money } from '../lib/format';
import { shipmentStatusLabels } from '../lib/labels';
import { P } from '../lib/permissions';

/* ================================== KARGO ================================== */

const TABS = [
  { key: 'firmalar', label: 'Kargo Firmaları ve Ücretleri' },
  { key: 'kolaygelsin', label: 'Kolay Gelsin Kargo Entegrasyonu' },
  { key: 'takip', label: 'Takip Bildirimi' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

const speedLabels: Record<ShippingSpeed, string> = {
  Standard: 'Standart',
  Express: 'Hızlı',
  SameDay: 'Aynı gün',
  NextDay: 'Ertesi gün',
  Economy: 'Ekonomik',
  StorePickup: 'Mağazadan teslim',
};

const rateTypeLabels: Record<ShippingRateType, string> = {
  Flat: 'Sabit ücret',
  PriceBased: 'Tutara göre',
  WeightBased: 'Ağırlığa göre',
  ItemBased: 'Adete göre',
  CarrierCalculated: 'Firma hesaplar',
  Free: 'Ücretsiz',
};

const scopeLabels: Record<ShippingZoneScope, string> = {
  Country: 'Ülke',
  Province: 'İl',
  PostalCode: 'Posta kodu',
  RestOfWorld: 'Diğer tüm bölgeler',
};

export function ShippingPage() {
  const [tab, setTab] = useState<TabKey>('firmalar');

  return (
    <div>
      <PageHeader title="Kargo" description="Kargo firmaları, özel kargo ücretleri ve teslimat ayarları" />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-ink-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
              tab === item.key
                ? 'border-ink-900 font-medium text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'firmalar' ? <CarriersTab /> : null}
      {tab === 'kolaygelsin' ? <KolayGelsinTab /> : null}
      {tab === 'takip' ? <TrackingTab /> : null}
    </div>
  );
}

const emptyZone: ShippingZoneUpsertDto = {
  name: '',
  scope: 'Country',
  isActive: true,
  isFallback: false,
  priority: 0,
  regions: [{ countryCode: 'TR' }],
  rates: [],
};

function emptyRate(): ShippingRateUpsertDto {
  return {
    name: 'Standart kargo',
    rateType: 'Flat',
    speed: 'Standard',
    price: 0,
    isActive: true,
    displayOrder: 0,
    allowsCashOnDelivery: false,
    cashOnDeliveryFee: 0,
    isTaxable: true,
  };
}

export function ZonesTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{ id: number | null; form: ShippingZoneUpsertDto } | null>(
    null,
  );
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const zones = useQuery({
    queryKey: ['admin', 'shipping-zones'],
    queryFn: () => adminShippingApi.zones(),
  });

  const carriers = useQuery({
    queryKey: ['admin', 'carriers'],
    queryFn: () => adminShippingApi.carriers(),
    staleTime: 5 * 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'shipping-zones'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminShippingApi.createZone(editing!.form)
        : adminShippingApi.updateZone(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Kargo bölgesi kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminShippingApi.deleteZone(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Bölge silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Bölge silinemedi.'),
  });

  if (zones.isLoading) return <PageLoader />;

  return (
    <div>
      {can(P.shipping.manage) ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing({ id: null, form: emptyZone })}
          >
            Yeni bölge
          </button>
        </div>
      ) : null}

      <div className="space-y-4">
        {(zones.data ?? []).map((zone) => (
          <section key={zone.id} className="card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">
                  {zone.name}
                  {zone.isFallback ? (
                    <span className="badge ml-2 bg-ink-100 text-ink-700">Yedek bölge</span>
                  ) : null}
                  {!zone.isActive ? (
                    <span className="badge ml-2 bg-ink-100 text-ink-600">Pasif</span>
                  ) : null}
                </h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  {scopeLabels[zone.scope]} · öncelik {zone.priority} ·{' '}
                  {zone.regions.map((region) => region.countryCode).join(', ') || 'bölge yok'}
                </p>
              </div>

              {can(P.shipping.manage) ? (
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() =>
                      setEditing({
                        id: zone.id,
                        form: {
                          name: zone.name,
                          scope: zone.scope,
                          isActive: zone.isActive,
                          isFallback: zone.isFallback,
                          priority: zone.priority,
                          regions: zone.regions.map((region) => ({
                            id: region.id,
                            countryCode: region.countryCode,
                            province: region.province,
                            district: region.district,
                            postalCodeFrom: region.postalCodeFrom,
                            postalCodeTo: region.postalCodeTo,
                          })),
                          rates: zone.rates.map((rate) => ({
                            id: rate.id,
                            name: rate.name,
                            description: rate.description,
                            rateType: rate.rateType,
                            speed: rate.speed,
                            price: rate.price,
                            carrierId: rate.carrierId,
                            minOrderSubtotal: rate.minOrderSubtotal,
                            maxOrderSubtotal: rate.maxOrderSubtotal,
                            freeAboveSubtotal: rate.freeAboveSubtotal,
                            minDeliveryDays: rate.minDeliveryDays,
                            maxDeliveryDays: rate.maxDeliveryDays,
                            isActive: rate.isActive,
                            displayOrder: rate.displayOrder,
                            allowsCashOnDelivery: rate.allowsCashOnDelivery,
                            cashOnDeliveryFee: rate.cashOnDeliveryFee,
                            isTaxable: true,
                          })),
                        },
                      })
                    }
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    className="btn-ghost btn-sm text-red-600"
                    onClick={() => setDeleteId(zone.id)}
                  >
                    Sil
                  </button>
                </div>
              ) : null}
            </div>

            {zone.rates.length > 0 ? (
              <div className="scroll-x mt-4">
                <table className="w-full min-w-[600px] text-sm">
                  <thead>
                    <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                      <th className="pb-2 font-semibold">Yöntem</th>
                      <th className="pb-2 font-semibold">Kural</th>
                      <th className="pb-2 font-semibold">Hız</th>
                      <th className="pb-2 text-right font-semibold">Ücret</th>
                      <th className="pb-2 text-right font-semibold">Ücretsiz eşiği</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {zone.rates.map((rate) => (
                      <tr key={rate.id}>
                        <td className="py-2 text-ink-800">
                          {rate.name}
                          {rate.carrierName ? (
                            <span className="ml-1.5 text-xs text-ink-400">{rate.carrierName}</span>
                          ) : null}
                        </td>
                        <td className="py-2 text-ink-600">{rateTypeLabels[rate.rateType]}</td>
                        <td className="py-2 text-ink-600">{speedLabels[rate.speed]}</td>
                        <td className="py-2 text-right font-medium">{money(rate.price)}</td>
                        <td className="py-2 text-right text-ink-500">
                          {rate.freeAboveSubtotal ? money(rate.freeAboveSubtotal) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-3 text-sm text-ink-500">Bu bölgede tanımlı ücret yok.</p>
            )}
          </section>
        ))}

        {(zones.data ?? []).length === 0 ? (
          <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
            Henüz kargo bölgesi tanımlanmadı.
          </p>
        ) : null}
      </div>

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni kargo bölgesi' : 'Bölgeyi düzenle'}
        onClose={() => setEditing(null)}
        size="xl"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-5">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Bölge adı" htmlFor="zn-name" required>
                <input
                  id="zn-name"
                  className="field"
                  value={editing.form.name}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                  }
                />
              </Field>

              <Field label="Kapsam" htmlFor="zn-scope">
                <select
                  id="zn-scope"
                  className="field"
                  value={editing.form.scope}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: { ...c.form, scope: event.target.value as ShippingZoneScope },
                        },
                    )
                  }
                >
                  {shippingZoneScopeValues.map((value) => (
                    <option key={value} value={value}>
                      {scopeLabels[value]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Öncelik" htmlFor="zn-priority" hint="Küçük değer önce denenir.">
                <input
                  id="zn-priority"
                  type="number"
                  className="field"
                  value={editing.form.priority}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, priority: Number(event.target.value) } },
                    )
                  }
                />
              </Field>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editing.form.isActive}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, isActive: event.target.checked } })
                  }
                />
                Aktif
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editing.form.isFallback}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, isFallback: event.target.checked } },
                    )
                  }
                />
                Yedek bölge (hiçbirine uymayan adresler)
              </label>
            </div>

            {/* Bolgeler */}
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-900">Kapsanan yerler</h3>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: { ...c.form, regions: [...c.form.regions, { countryCode: 'TR' }] },
                        },
                    )
                  }
                >
                  Yer ekle
                </button>
              </div>

              <ul className="space-y-2">
                {editing.form.regions.map((region, index) => (
                  <li key={index} className="grid gap-2 sm:grid-cols-[100px_1fr_1fr_auto]">
                    <input
                      className="field py-2 text-sm"
                      placeholder="TR"
                      maxLength={2}
                      aria-label="Ülke kodu"
                      value={region.countryCode}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                regions: c.form.regions.map((item, i) =>
                                  i === index
                                    ? { ...item, countryCode: event.target.value.toUpperCase() }
                                    : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <input
                      className="field py-2 text-sm"
                      placeholder="İl (boşsa tüm ülke)"
                      aria-label="İl"
                      value={region.province ?? ''}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                regions: c.form.regions.map((item, i) =>
                                  i === index ? { ...item, province: event.target.value } : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <input
                      className="field py-2 text-sm"
                      placeholder="İlçe"
                      aria-label="İlçe"
                      value={region.district ?? ''}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                regions: c.form.regions.map((item, i) =>
                                  i === index ? { ...item, district: event.target.value } : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <button
                      type="button"
                      className="btn-ghost btn-sm text-red-600"
                      onClick={() =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                regions: c.form.regions.filter((_, i) => i !== index),
                              },
                            },
                        )
                      }
                    >
                      Sil
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ucret kurallari */}
            <div className="rounded-lg border border-ink-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-900">Ücret kuralları</h3>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, rates: [...c.form.rates, emptyRate()] } },
                    )
                  }
                >
                  Ücret ekle
                </button>
              </div>

              <ul className="space-y-4">
                {editing.form.rates.map((rate, index) => (
                  <li key={index} className="rounded-lg bg-ink-50 p-3">
                    <div className="grid gap-3 sm:grid-cols-4">
                      <Field label="Ad" htmlFor={`rt-name-${index}`}>
                        <input
                          id={`rt-name-${index}`}
                          className="field py-2 text-sm"
                          value={rate.name}
                          onChange={(event) => updateRate(setEditing, index, { name: event.target.value })}
                        />
                      </Field>

                      <Field label="Kural" htmlFor={`rt-type-${index}`}>
                        <select
                          id={`rt-type-${index}`}
                          className="field py-2 text-sm"
                          value={rate.rateType}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              rateType: event.target.value as ShippingRateType,
                            })
                          }
                        >
                          {shippingRateTypeValues.map((value) => (
                            <option key={value} value={value}>
                              {rateTypeLabels[value]}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Hız" htmlFor={`rt-speed-${index}`}>
                        <select
                          id={`rt-speed-${index}`}
                          className="field py-2 text-sm"
                          value={rate.speed}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              speed: event.target.value as ShippingSpeed,
                            })
                          }
                        >
                          {shippingSpeedValues.map((value) => (
                            <option key={value} value={value}>
                              {speedLabels[value]}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Ücret" htmlFor={`rt-price-${index}`}>
                        <input
                          id={`rt-price-${index}`}
                          type="number"
                          step="0.01"
                          min={0}
                          className="field py-2 text-sm"
                          value={rate.price}
                          onChange={(event) =>
                            updateRate(setEditing, index, { price: Number(event.target.value) })
                          }
                        />
                      </Field>

                      <Field label="Kargo firması" htmlFor={`rt-carrier-${index}`}>
                        <select
                          id={`rt-carrier-${index}`}
                          className="field py-2 text-sm"
                          value={rate.carrierId ?? ''}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              carrierId: event.target.value ? Number(event.target.value) : null,
                            })
                          }
                        >
                          <option value="">Seçilmedi</option>
                          {(carriers.data ?? []).map((carrier) => (
                            <option key={carrier.id} value={carrier.id}>
                              {carrier.name}
                            </option>
                          ))}
                        </select>
                      </Field>

                      <Field label="Ücretsiz eşiği" htmlFor={`rt-free-${index}`}>
                        <input
                          id={`rt-free-${index}`}
                          type="number"
                          step="0.01"
                          min={0}
                          className="field py-2 text-sm"
                          value={rate.freeAboveSubtotal ?? ''}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              freeAboveSubtotal: event.target.value
                                ? Number(event.target.value)
                                : null,
                            })
                          }
                        />
                      </Field>

                      <Field label="En az teslim (gün)" htmlFor={`rt-min-${index}`}>
                        <input
                          id={`rt-min-${index}`}
                          type="number"
                          min={0}
                          className="field py-2 text-sm"
                          value={rate.minDeliveryDays ?? ''}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              minDeliveryDays: event.target.value ? Number(event.target.value) : null,
                            })
                          }
                        />
                      </Field>

                      <Field label="En çok teslim (gün)" htmlFor={`rt-max-${index}`}>
                        <input
                          id={`rt-max-${index}`}
                          type="number"
                          min={0}
                          className="field py-2 text-sm"
                          value={rate.maxDeliveryDays ?? ''}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              maxDeliveryDays: event.target.value ? Number(event.target.value) : null,
                            })
                          }
                        />
                      </Field>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-ink-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-ink-300 text-brand-600"
                          checked={rate.allowsCashOnDelivery}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              allowsCashOnDelivery: event.target.checked,
                            })
                          }
                        />
                        Kapıda ödeme
                      </label>

                      {rate.allowsCashOnDelivery ? (
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          className="field w-28 py-1.5 text-sm"
                          placeholder="Ek ücret"
                          aria-label="Kapıda ödeme ücreti"
                          value={rate.cashOnDeliveryFee}
                          onChange={(event) =>
                            updateRate(setEditing, index, {
                              cashOnDeliveryFee: Number(event.target.value),
                            })
                          }
                        />
                      ) : null}

                      <label className="flex items-center gap-2 text-sm text-ink-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-ink-300 text-brand-600"
                          checked={rate.isActive}
                          onChange={(event) =>
                            updateRate(setEditing, index, { isActive: event.target.checked })
                          }
                        />
                        Aktif
                      </label>

                      <button
                        type="button"
                        className="btn-ghost btn-sm ml-auto text-red-600"
                        onClick={() =>
                          setEditing(
                            (c) =>
                              c && {
                                ...c,
                                form: { ...c.form, rates: c.form.rates.filter((_, i) => i !== index) },
                              },
                          )
                        }
                      >
                        Ücreti kaldır
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Bölgeyi sil"
        message="Bölge ve altındaki ücret kuralları silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

function updateRate(
  setEditing: React.Dispatch<
    React.SetStateAction<{ id: number | null; form: ShippingZoneUpsertDto } | null>
  >,
  index: number,
  patch: Partial<ShippingRateUpsertDto>,
) {
  setEditing(
    (current) =>
      current && {
        ...current,
        form: {
          ...current.form,
          rates: current.form.rates.map((rate, i) => (i === index ? { ...rate, ...patch } : rate)),
        },
      },
  );
}

/* ----------------------------- Kargo firmalari ----------------------------- */

const emptyCarrier: CarrierUpsertDto = {
  name: '',
  code: '',
  isActive: true,
  hasApiIntegration: false,
  supportsCashOnDelivery: false,
  supportsLabelGeneration: false,
  displayOrder: 0,
  price: 0,
  freeAboveSubtotal: null,
  minDeliveryDays: 1,
  maxDeliveryDays: 3,
  allowsCashOnDelivery: false,
  cashOnDeliveryFee: 0,
};

function CarriersTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{ id: number | null; form: CarrierUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const carriers = useQuery({
    queryKey: ['admin', 'carriers'],
    queryFn: () => adminShippingApi.carriers(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'carriers'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminShippingApi.createCarrier(editing!.form)
        : adminShippingApi.updateCarrier(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Kargo firması ve ücret ayarları kaydedildi.');
      setEditing(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Kargo firması kaydedilemedi.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminShippingApi.deleteCarrier(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Firma silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Firma silinemedi.'),
  });

  if (carriers.isLoading) return <PageLoader />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-ink-900">Kargo Firmaları ve Fiyat Tarifeleri</h3>
          <p className="text-xs text-ink-500">
            Müşterilerinizin sipariş verirken seçeceği kargo firmaları, gönderim ücretleri ve teslimat süreleri.
          </p>
        </div>
        {can(P.shipping.manage) ? (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing({ id: null, form: { ...emptyCarrier } })}
          >
            + Yeni Kargo Firması Ekle
          </button>
        ) : null}
      </div>

      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(carriers.data ?? []).map((carrier) => (
          <li key={carrier.id} className="card p-5 flex flex-col justify-between shadow-sm hover:shadow transition border border-ink-200">
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg bg-ink-50 p-1 border border-ink-100">
                    {carrier.logoUrl ? (
                      <img src={carrier.logoUrl} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <span className="text-sm font-bold text-ink-400">{carrier.name[0]}</span>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-ink-900">{carrier.name}</p>
                    <p className="font-mono text-xs text-ink-400">{carrier.code}</p>
                  </div>
                </div>

                {carrier.isActive ? (
                  <span className="badge bg-emerald-50 text-emerald-700 font-medium">Aktif</span>
                ) : (
                  <span className="badge bg-ink-100 text-ink-600">Pasif</span>
                )}
              </div>

              {/* Fiyatlandırma & Teslimat Özeti */}
              <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-ink-50/80 p-3 text-xs border border-ink-100">
                <div>
                  <span className="text-ink-500 block">Kargo Ücreti:</span>
                  <span className="font-bold text-ink-900 text-sm">
                    {carrier.price <= 0 ? (
                      <span className="text-emerald-700">Ücretsiz Kargo</span>
                    ) : (
                      money(carrier.price)
                    )}
                  </span>
                </div>
                <div>
                  <span className="text-ink-500 block">Ücretsiz Kargo Şartı:</span>
                  <span className="font-medium text-ink-800">
                    {carrier.freeAboveSubtotal && carrier.freeAboveSubtotal > 0
                      ? `${money(carrier.freeAboveSubtotal)} ve üzeri`
                      : 'Limit Yok'}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="text-ink-500 block">Teslimat Süresi:</span>
                  <span className="font-medium text-ink-800">
                    {carrier.minDeliveryDays ?? 1} - {carrier.maxDeliveryDays ?? 3} iş günü
                  </span>
                </div>
                <div className="mt-2">
                  <span className="text-ink-500 block">Kapıda Ödeme:</span>
                  <span className="font-medium text-ink-800">
                    {carrier.allowsCashOnDelivery || carrier.supportsCashOnDelivery
                      ? carrier.cashOnDeliveryFee > 0
                        ? `+${money(carrier.cashOnDeliveryFee)} ücretli`
                        : 'Ücretsiz'
                      : 'Desteklenmiyor'}
                  </span>
                </div>
              </div>

              <dl className="mt-3 space-y-1 text-xs text-ink-500">
                {carrier.hasApiIntegration ? (
                  <div className="flex justify-between">
                    <dt>API Durumu</dt>
                    <dd className="font-medium text-emerald-600">Entegrasyon Tanımlı</dd>
                  </div>
                ) : null}
                {carrier.accountNumber ? (
                  <div className="flex justify-between">
                    <dt>Müşteri No</dt>
                    <dd className="font-mono">{carrier.accountNumber}</dd>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-1 pt-1">
                  {carrier.supportsLabelGeneration ? (
                    <span className="badge bg-ink-100 text-ink-700">Etiket üretimi</span>
                  ) : null}
                  {carrier.trackingUrlTemplate ? (
                    <span className="badge bg-ink-100 text-ink-700">Otomatik Takip</span>
                  ) : null}
                </div>
              </dl>
            </div>

            {can(P.shipping.manage) ? (
              <div className="mt-4 flex gap-2 border-t border-ink-100 pt-3">
                <button
                  type="button"
                  className="btn-secondary btn-sm flex-1"
                  onClick={() =>
                    setEditing({
                      id: carrier.id,
                      form: {
                        name: carrier.name,
                        code: carrier.code,
                        logoUrl: carrier.logoUrl,
                        isActive: carrier.isActive,
                        trackingUrlTemplate: carrier.trackingUrlTemplate,
                        hasApiIntegration: carrier.hasApiIntegration,
                        apiKey: null,
                        accountNumber: carrier.accountNumber,
                        supportsCashOnDelivery: carrier.supportsCashOnDelivery || carrier.allowsCashOnDelivery,
                        supportsLabelGeneration: carrier.supportsLabelGeneration,
                        displayOrder: carrier.displayOrder,
                        price: carrier.price ?? 0,
                        freeAboveSubtotal: carrier.freeAboveSubtotal ?? null,
                        minDeliveryDays: carrier.minDeliveryDays ?? 1,
                        maxDeliveryDays: carrier.maxDeliveryDays ?? 3,
                        allowsCashOnDelivery: carrier.allowsCashOnDelivery || carrier.supportsCashOnDelivery,
                        cashOnDeliveryFee: carrier.cashOnDeliveryFee ?? 0,
                      },
                    })
                  }
                >
                  Fiyat ve Ayarları Düzenle
                </button>
                <button
                  type="button"
                  className="btn-ghost btn-sm text-red-600"
                  onClick={() => setDeleteId(carrier.id)}
                >
                  Sil
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {(carriers.data ?? []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
          Kargo firması tanımlanmamış. Yukarıdaki butonu kullanarak kargo firması ekleyin.
        </p>
      ) : null}

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni Kargo Firması ve Ücreti' : `${editing?.form.name || 'Firma'} - Ücret ve Ayarlar`}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name || !editing?.form.code}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-5">
            <FormErrors error={save.error} />

            {/* Bölüm 1: Temel Bilgiler */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-3">
                Kargo Firması Bilgileri
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Firma Adı" htmlFor="cr-name" required>
                  <input
                    id="cr-name"
                    className="field"
                    placeholder="Örn. Sürat Kargo"
                    value={editing.form.name}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Kod" htmlFor="cr-code" hint="Küçük harf ve benzersiz olmalıdır (örn: surat)" required>
                  <input
                    id="cr-code"
                    className="field font-mono text-sm"
                    placeholder="surat"
                    value={editing.form.code}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, code: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Logo URL" htmlFor="cr-logo" hint="Kargo firması logo görsel bağlantısı">
                  <input
                    id="cr-logo"
                    className="field text-sm"
                    placeholder="https://.../logo.png"
                    value={editing.form.logoUrl ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, logoUrl: event.target.value } })
                    }
                  />
                </Field>

                <Field label="Sıralama" htmlFor="cr-order" hint="Listeleme sırası (küçük olan önce)">
                  <input
                    id="cr-order"
                    type="number"
                    className="field"
                    value={editing.form.displayOrder}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, displayOrder: Number(event.target.value) || 0 } })
                    }
                  />
                </Field>
              </div>
            </div>

            {/* Bölüm 2: Fiyatlandırma ve Teslimat Süresi */}
            <div className="rounded-xl border border-ink-100 bg-ink-50/50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-700 mb-3">
                Kargo Fiyatlandırması ve Teslimat Süresi
              </h4>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Kargo Ücreti (₺)"
                  htmlFor="cr-price"
                  hint="Siparişte müşteriye yansıtılacak kargo ücreti (0 ise ücretsiz kargo)"
                  required
                >
                  <input
                    id="cr-price"
                    type="number"
                    step="0.01"
                    min="0"
                    className="field font-semibold"
                    value={editing.form.price ?? 0}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, price: Number(event.target.value) || 0 } })
                    }
                  />
                </Field>

                <Field
                  label="Ücretsiz Kargo Alt Limiti (₺)"
                  htmlFor="cr-free"
                  hint="Bu tutar ve üzerindeki sepetlerde kargo bedava olur. Boş bırakırsanız her zaman ücretli olur."
                >
                  <input
                    id="cr-free"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Örn: 750"
                    className="field"
                    value={editing.form.freeAboveSubtotal ?? ''}
                    onChange={(event) =>
                      setEditing((c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            freeAboveSubtotal: event.target.value === '' ? null : Number(event.target.value),
                          },
                        },
                      )
                    }
                  />
                </Field>

                <Field label="Minimum Teslimat (Gün)" htmlFor="cr-mindays">
                  <input
                    id="cr-mindays"
                    type="number"
                    min="1"
                    className="field"
                    value={editing.form.minDeliveryDays ?? 1}
                    onChange={(event) =>
                      setEditing((c) =>
                        c && { ...c, form: { ...c.form, minDeliveryDays: Number(event.target.value) || 1 } },
                      )
                    }
                  />
                </Field>

                <Field label="Maksimum Teslimat (Gün)" htmlFor="cr-maxdays">
                  <input
                    id="cr-maxdays"
                    type="number"
                    min="1"
                    className="field"
                    value={editing.form.maxDeliveryDays ?? 3}
                    onChange={(event) =>
                      setEditing((c) =>
                        c && { ...c, form: { ...c.form, maxDeliveryDays: Number(event.target.value) || 3 } },
                      )
                    }
                  />
                </Field>
              </div>
            </div>

            {/* Bölüm 3: Kapıda Ödeme Seçenekleri */}
            <div className="rounded-xl border border-ink-100 bg-ink-50/50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-600 mb-3">
                Kapıda Ödeme Ayarları
              </h4>
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-medium text-ink-800">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={editing.form.allowsCashOnDelivery || editing.form.supportsCashOnDelivery}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: {
                              ...c.form,
                              allowsCashOnDelivery: event.target.checked,
                              supportsCashOnDelivery: event.target.checked,
                            },
                          },
                      )
                    }
                  />
                  Bu kargo firmasında kapıda ödeme seçeneği aktif olsun
                </label>

                {(editing.form.allowsCashOnDelivery || editing.form.supportsCashOnDelivery) ? (
                  <div className="pt-2 max-w-xs">
                    <Field
                      label="Kapıda Ödeme Hizmet Bedeli (₺)"
                      htmlFor="cr-codfee"
                      hint="Kapıda ödeme seçildiğinde kargo ücretine eklenen tahsilat komisyonu."
                    >
                      <input
                        id="cr-codfee"
                        type="number"
                        step="0.01"
                        min="0"
                        className="field"
                        value={editing.form.cashOnDeliveryFee ?? 0}
                        onChange={(event) =>
                          setEditing((c) =>
                            c && {
                              ...c,
                              form: { ...c.form, cashOnDeliveryFee: Number(event.target.value) || 0 },
                            },
                          )
                        }
                      />
                    </Field>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Bölüm 4: Takip Linki ve API Bilgileri */}
            <div>
              <Field
                label="Kargo Takip Adresi Kalıbı"
                htmlFor="cr-tracking"
                hint="{trackingNumber} yer tutucusunu kullanın."
              >
                <input
                  id="cr-tracking"
                  className="field font-mono text-xs"
                  placeholder="https://kargo.com/takip?no={trackingNumber}"
                  value={editing.form.trackingUrlTemplate ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && { ...c, form: { ...c.form, trackingUrlTemplate: event.target.value } },
                    )
                  }
                />
              </Field>
            </div>

            {/* Bölüm 5: Gelişmiş API Entegrasyonu & Durum */}
            <div className="space-y-3 pt-2 border-t border-ink-100">
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editing.form.hasApiIntegration}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, hasApiIntegration: event.target.checked } },
                    )
                  }
                />
                Firma ile doğrudan API Entegrasyonu var
              </label>

              {editing.form.hasApiIntegration ? (
                <div className="grid gap-4 sm:grid-cols-2 rounded-lg bg-ink-50 p-3">
                  <Field label="API Adresi" htmlFor="cr-apiurl">
                    <input
                      id="cr-apiurl"
                      className="field text-sm"
                      value={editing.form.apiBaseUrl ?? ''}
                      onChange={(event) =>
                        setEditing(
                          (c) => c && { ...c, form: { ...c.form, apiBaseUrl: event.target.value } },
                        )
                      }
                    />
                  </Field>

                  <Field
                    label="API Anahtarı"
                    htmlFor="cr-apikey"
                    hint="Boş bırakırsanız mevcut anahtar korunur."
                  >
                    <input
                      id="cr-apikey"
                      type="password"
                      className="field text-sm"
                      value={editing.form.apiKey ?? ''}
                      onChange={(event) =>
                        setEditing((c) => c && { ...c, form: { ...c.form, apiKey: event.target.value } })
                      }
                    />
                  </Field>

                  <Field label="Müşteri Numarası" htmlFor="cr-account">
                    <input
                      id="cr-account"
                      className="field text-sm"
                      value={editing.form.accountNumber ?? ''}
                      onChange={(event) =>
                        setEditing(
                          (c) => c && { ...c, form: { ...c.form, accountNumber: event.target.value } },
                        )
                      }
                    />
                  </Field>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={editing.form.isActive}
                    onChange={(event) =>
                      setEditing(
                        (c) => c && { ...c, form: { ...c.form, isActive: event.target.checked } },
                      )
                    }
                  />
                  Aktif (Sepette ve ödemede müşterilere görünsün)
                </label>

                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-ink-300 text-brand-600"
                    checked={editing.form.supportsLabelGeneration}
                    onChange={(event) =>
                      setEditing(
                        (c) =>
                          c && {
                            ...c,
                            form: { ...c.form, supportsLabelGeneration: event.target.checked },
                          },
                      )
                    }
                  />
                  Kargo barkod / etiketi üretebilir
                </label>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Kargo firmasını sil"
        message="Bu kargo firmasını ve tanımlı kargo ücretini silmek istediğinize emin misiniz?"
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ---------------------------- Takip bildirimi ---------------------------- */

function TrackingTab() {
  const toast = useToast();

  const [form, setForm] = useState<TrackingEventInput>({
    trackingNumber: '',
    status: 'InTransit',
    description: '',
    location: '',
    occurredAt: new Date().toISOString(),
  });
  const [secret, setSecret] = useState('');

  const push = useMutation({
    mutationFn: () => shippingWebhookApi.pushTrackingEvent(form, secret),
    onSuccess: (message) => {
      toast.success(message ?? 'Takip olayı işlendi.');
      setForm((current) => ({ ...current, description: '', location: '' }));
    },
    onError: (error) => toast.fromError(error, 'Takip olayı işlenemedi.'),
  });

  return (
    <div className="max-w-2xl">
      <div className="mb-5 rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
        <p className="font-medium">Bu ekran kargo firmasının bildirim ucunu elle tetikler.</p>
        <p className="mt-1">
          Normalde taşıyıcının sunucusu <code className="font-mono">/api/shipping-webhooks/tracking</code>{' '}
          adresini kendisi çağırır. Bildirim gelmediğinde personel durumu buradan işleyebilir.
          İstek, taşıyıcı gizli anahtarıyla imzalanır.
        </p>
      </div>

      <form
        className="card space-y-4 p-5"
        onSubmit={(event) => {
          event.preventDefault();
          push.mutate();
        }}
      >
        <FormErrors error={push.error} />

        <Field label="Taşıyıcı gizli anahtarı" htmlFor="tw-secret" required>
          <input
            id="tw-secret"
            type="password"
            className="field"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Takip numarası" htmlFor="tw-number" required>
            <input
              id="tw-number"
              className="field font-mono text-sm"
              required
              value={form.trackingNumber}
              onChange={(event) =>
                setForm((c) => ({ ...c, trackingNumber: event.target.value }))
              }
            />
          </Field>

          <Field label="Durum" htmlFor="tw-status" required>
            <select
              id="tw-status"
              className="field"
              value={form.status}
              onChange={(event) =>
                setForm((c) => ({ ...c, status: event.target.value as ShipmentTrackingStatus }))
              }
            >
              {shipmentTrackingStatusValues.map((value) => (
                <option key={value} value={value}>
                  {shipmentStatusLabels[value].text}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Açıklama" htmlFor="tw-desc">
            <input
              id="tw-desc"
              className="field"
              placeholder="Paket dağıtım şubesine ulaştı"
              value={form.description ?? ''}
              onChange={(event) => setForm((c) => ({ ...c, description: event.target.value }))}
            />
          </Field>

          <Field label="Konum" htmlFor="tw-location">
            <input
              id="tw-location"
              className="field"
              placeholder="İstanbul Anadolu Şubesi"
              value={form.location ?? ''}
              onChange={(event) => setForm((c) => ({ ...c, location: event.target.value }))}
            />
          </Field>
        </div>

        <button
          type="submit"
          className="btn-primary"
          disabled={push.isPending || !secret || !form.trackingNumber}
        >
          {push.isPending ? <Spinner className="h-4 w-4" /> : null}
          Olayı işle
        </button>
      </form>
    </div>
  );
}

/* ============================ KOLAY GELSİN KARGO ENTEGRASYONU ============================ */

function KolayGelsinTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const isEditable = can(P.shipping.manage);

  const [testNumber, setTestNumber] = useState('');
  const [trackingModalData, setTrackingModalData] = useState<KolayGelsinTrackingResultDto | null>(null);

  // Kolay Gelsin ayarlarını çek
  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin', 'shipping', 'kolay-gelsin', 'settings'],
    queryFn: () => adminKolayGelsinApi.getSettings(),
  });

  // Türkiye İlleri ve Seçili İl İlçeleri
  const { data: cities } = useQuery({
    queryKey: ['admin', 'shipping', 'kolay-gelsin', 'cities'],
    queryFn: () => adminKolayGelsinApi.getCities(),
  });

  // Form durumu
  const [form, setForm] = useState<KolayGelsinSettingsUpsertDto>({
    isEnabled: false,
    testMode: true,
    apiBaseUrl: 'https://api.sendeo.com.tr',
    musteri: '',
    sifre: '',
    senderName: 'E-Ticaret Depo',
    senderAddress: '',
    senderCity: 'İstanbul',
    senderCityId: 34,
    senderDistrict: 'Kadıköy',
    senderDistrictId: 34288,
    senderPhone: '',
    senderEmail: '',
    autoSyncEnabled: true,
    syncIntervalMinutes: 30,
    returnCodePrefix: 'KG-IAD-',
  });

  const { data: districts } = useQuery({
    queryKey: ['admin', 'shipping', 'kolay-gelsin', 'districts', form.senderCityId],
    queryFn: () => adminKolayGelsinApi.getDistricts(form.senderCityId),
    enabled: form.senderCityId > 0,
  });

  const { data: depotNeighborhoods = [], isLoading: loadingNeighborhoods } = useQuery({
    queryKey: ['admin', 'shipping', 'depot-neighborhoods', form.senderCityId, form.senderDistrict],
    queryFn: () => adminShippingLocationsApi.getNeighborhoods(form.senderCityId, form.senderDistrict || undefined),
    enabled: !!form.senderCityId && !!form.senderDistrict,
  });

  useEffect(() => {
    if (settings) {
      setForm({
        isEnabled: settings.isEnabled,
        testMode: settings.testMode,
        apiBaseUrl: settings.apiBaseUrl ?? 'https://api.sendeo.com.tr',
        musteri: settings.musteri ?? '',
        sifre: '',
        senderName: settings.senderName ?? 'E-Ticaret Depo',
        senderAddress: settings.senderAddress ?? '',
        senderCity: settings.senderCity ?? 'İstanbul',
        senderCityId: settings.senderCityId || 34,
        senderDistrict: settings.senderDistrict ?? 'Kadıköy',
        senderDistrictId: settings.senderDistrictId || 34288,
        senderPhone: settings.senderPhone ?? '',
        senderEmail: settings.senderEmail ?? '',
        autoSyncEnabled: settings.autoSyncEnabled,
        syncIntervalMinutes: settings.syncIntervalMinutes ?? 30,
        returnCodePrefix: settings.returnCodePrefix ?? 'KG-IAD-',
      });
    }
  }, [settings]);

  // Ayarları kaydetme
  const save = useMutation({
    mutationFn: () => adminKolayGelsinApi.updateSettings(form),
    onSuccess: () => {
      toast.success('Kolay Gelsin kargo ve barkod ayarları başarıyla kaydedildi.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'shipping', 'kolay-gelsin', 'settings'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'shipping', 'carriers'] });
    },
    onError: (err) => toast.fromError(err, 'Ayarlar kaydedilemedi.'),
  });

  // Canlı bağlantı testi
  const testConn = useMutation({
    mutationFn: () => adminKolayGelsinApi.testConnection(),
    onSuccess: (message) => {
      toast.success(message ?? 'Kolay Gelsin API bağlantısı başarıyla doğrulandı.');
    },
    onError: (err) => toast.fromError(err, 'Bağlantı testi başarısız.'),
  });

  // Durum senkronizasyonu
  const syncAll = useMutation({
    mutationFn: () => adminKolayGelsinApi.sync(),
    onSuccess: (data) => {
      const msg = `Senkronizasyon tamamlandı: ${data.updatedShipmentsCount} sevkiyat güncellendi (${data.deliveredShipmentsCount} teslim edildi).`;
      toast.success(msg);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'shipping', 'kolay-gelsin', 'settings'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'returns'] });
    },
    onError: (err) => toast.fromError(err, 'Kargo senkronizasyonu yapılamadı.'),
  });

  // Takip sorgulama
  const queryTracking = useMutation({
    mutationFn: (trackingNo: string) => adminKolayGelsinApi.getTracking(trackingNo),
    onSuccess: (data) => {
      setTrackingModalData(data);
    },
    onError: (err) => toast.fromError(err, 'Kargo takip bilgisi sorgulanamadı.'),
  });

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* 1. ÜST DURUM PANELİ */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card p-4">
          <p className="text-xs font-medium text-ink-500">Entegrasyon Durumu</p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-block h-3 w-3 rounded-full ${
                settings?.isEnabled ? 'bg-emerald-500' : 'bg-ink-300'
              }`}
            />
            <p className="text-base font-semibold text-ink-900">
              {settings?.isEnabled ? 'Aktif (Tek Taşıyıcı)' : 'Devre Dışı'}
            </p>
          </div>
          <p className="mt-1 text-xs text-ink-400">
            {settings?.isEnabled ? 'Tüm siparişler Kolay Gelsin ile sevk edilir' : 'Kargo entegrasyonu kapalı'}
          </p>
        </div>

        <div className="card p-4">
          <p className="text-xs font-medium text-ink-500">Çalışma Modu</p>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-block h-3 w-3 rounded-full ${
                settings?.testMode ? 'bg-amber-500' : 'bg-emerald-500'
              }`}
            />
            <p className="text-base font-semibold text-ink-900">
              {settings?.testMode ? 'Test (Simülasyon)' : 'Canlı API'}
            </p>
          </div>
          <p className="mt-1 text-xs text-ink-400">
            {settings?.testMode ? 'Sanal takip ve barkod testi devrede' : 'Canlı Kolay Gelsin REST API'}
          </p>
        </div>

        <div className="card p-4">
          <p className="text-xs font-medium text-ink-500">Otomatik Durum Senkronizasyonu</p>
          <p className="mt-2 text-base font-semibold text-ink-900">
            {settings?.autoSyncEnabled ? `Her ${settings.syncIntervalMinutes} dakikada bir` : 'Elle tetikleme'}
          </p>
          <p className="mt-1 text-xs text-ink-400">Kolay Gelsin 30 dk önbellek standardına uygun</p>
        </div>

        <div className="card p-4">
          <p className="text-xs font-medium text-ink-500">Son Senkronizasyon</p>
          <p className="mt-2 text-base font-semibold text-ink-900">
            {settings?.lastSyncAt ? formatDateTime(settings.lastSyncAt) : 'Henüz çalıştırılmadı'}
          </p>
          <p className="mt-1 text-xs text-ink-400">Yoldaki tüm gönderiler güncellenir</p>
        </div>
      </div>

      {/* 2. HIZLI İŞLEM VE TETİKLEME PANELİ */}
      <div className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink-100 pb-4">
          <div>
            <h2 className="text-base font-semibold text-ink-900">Kolay Gelsin Servisleri ve Hızlı İşlemler</h2>
            <p className="text-xs text-ink-500">
              API bağlantısını test edebilir veya dağıtımdaki tüm paketlerin güncel durumunu Kolay Gelsin'den çekebilirsiniz.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary text-sm"
              disabled={testConn.isPending}
              onClick={() => testConn.mutate()}
            >
              {testConn.isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
              Bağlantıyı Test Et
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={syncAll.isPending}
              onClick={() => syncAll.mutate()}
            >
              {syncAll.isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
              Kargo Durumlarını Şimdi Güncelle
            </button>
          </div>
        </div>

        {/* Hızlı Takip Sorgulama */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Field
              label="Kolay Gelsin Takip No / Referans Sorgula"
              htmlFor="kg-test-no"
              hint="Örn: 9124356789012 veya SP-1042-F1"
            >
              <input
                id="kg-test-no"
                type="text"
                className="field font-mono text-sm uppercase"
                placeholder="Takip veya referans numarasını giriniz..."
                value={testNumber}
                onChange={(e) => setTestNumber(e.target.value)}
              />
            </Field>
          </div>
          <button
            type="button"
            className="btn-secondary text-sm mb-1"
            disabled={!testNumber.trim() || queryTracking.isPending}
            onClick={() => queryTracking.mutate(testNumber.trim())}
          >
            {queryTracking.isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
            Canlı Durumu Sorgula
          </button>
        </div>
      </div>

      {/* 3. ENTEGRASYON AYARLARI FORMU */}
      <section className="card p-6">
        <div className="border-b border-ink-100 pb-4 mb-5">
          <h2 className="text-base font-semibold text-ink-900">Kolay Gelsin API ve Gönderici Depo Ayarları</h2>
          <p className="text-xs text-ink-500 mt-0.5">
            Kolay Gelsin (Sendeo) web servis sözleşme ve depo çıkış adres bilgilerini buradan yapılandırabilirsiniz.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-6"
        >
          {/* Durum Anahtarları */}
          <div className="grid gap-4 sm:grid-cols-2 rounded-lg bg-ink-50 p-4 border border-ink-100">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                disabled={!isEditable}
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                checked={form.isEnabled}
                onChange={(e) => setForm((c) => ({ ...c, isEnabled: e.target.checked }))}
              />
              <div>
                <span className="text-sm font-semibold text-ink-900">Kolay Gelsin Kargo Entegrasyonunu Etkinleştir</span>
                <p className="text-xs text-ink-500 mt-0.5">
                  Açıldığında sipariş detayında otomatik sevk emri ve barkod yazdırma butonu aktifleşir.
                </p>
              </div>
            </label>

            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                disabled={!isEditable}
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-amber-600 focus:ring-amber-500"
                checked={form.testMode}
                onChange={(e) => setForm((c) => ({ ...c, testMode: e.target.checked }))}
              />
              <div>
                <span className="text-sm font-semibold text-amber-900">Test / Simülasyon Modu</span>
                <p className="text-xs text-amber-700 mt-0.5">
                  İşaretliyken gerçek kurye çağırmadan sanal kargo barkodu ve takip süreçlerini test edebilirsiniz.
                </p>
              </div>
            </label>
          </div>

          {/* Kolay Gelsin API Kimlik Bilgileri */}
          <div>
            <h3 className="text-sm font-semibold text-ink-900 mb-3">1. Web Servis ve API Bağlantısı</h3>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Field
                  label="API Taban Adresi (Base URL)"
                  htmlFor="kg-api-url"
                  hint="Kolay Gelsin / Sendeo web servis bağlantı adresi (İsteğinize göre değiştirebilirsiniz)"
                  required={form.isEnabled}
                >
                  <input
                    id="kg-api-url"
                    type="url"
                    disabled={!isEditable}
                    className="field font-mono text-sm"
                    value={form.apiBaseUrl ?? 'https://api.sendeo.com.tr'}
                    onChange={(e) => setForm((c) => ({ ...c, apiBaseUrl: e.target.value }))}
                    placeholder="https://api.sendeo.com.tr veya https://apiintg.kolaygelsin.com"
                  />
                </Field>
              </div>

              <Field
                label="API Müşteri Kodu (musteri)"
                htmlFor="kg-musteri"
                hint="Kolay Gelsin tarafından tahsis edilen web servis kullanıcı / müşteri adı"
                required={form.isEnabled && !form.testMode}
              >
                <input
                  id="kg-musteri"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono text-sm"
                  value={form.musteri ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, musteri: e.target.value }))}
                  placeholder="Örn: TEST veya KURUMSAL_KOD"
                />
              </Field>

              <Field
                label="API Şifresi (sifre)"
                htmlFor="kg-sifre"
                hint={settings?.maskedSifre ? 'Mevcut şifre korunuyor (değiştirmek için yeni şifre giriniz)' : 'Kolay Gelsin web servis şifresi'}
                required={form.isEnabled && !form.testMode && !settings?.maskedSifre}
              >
                <input
                  id="kg-sifre"
                  type="password"
                  disabled={!isEditable}
                  className="field font-mono text-sm"
                  value={form.sifre ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, sifre: e.target.value }))}
                  placeholder={settings?.maskedSifre ? '••••••••' : 'Şifre giriniz'}
                />
              </Field>
            </div>
          </div>

          {/* Gönderici Depo Bilgileri */}
          <div className="border-t border-ink-100 pt-5">
            <h3 className="text-sm font-semibold text-ink-900 mb-1">2. Gönderici (Çıkış Depo) Bilgileri</h3>
            <p className="text-xs text-ink-500 mb-4">
              Kolay Gelsin sevk emirlerinde kargonun teslim alınacağı çıkış depo bilgileridir. İl ve ilçe seçildiğinde Kolay Gelsin kodları otomatik eşleşir.
            </p>

            <div className="grid gap-5 sm:grid-cols-2">
              <Field
                label="Firma / Gönderici Adı"
                htmlFor="kg-sender-name"
                hint="Paketin üstündeki çıkış firma unvanı"
                required={form.isEnabled}
              >
                <input
                  id="kg-sender-name"
                  type="text"
                  disabled={!isEditable}
                  className="field text-sm"
                  value={form.senderName ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, senderName: e.target.value }))}
                  placeholder="Örn: Mağaza A.Ş."
                />
              </Field>

              <Field
                label="Gönderici Telefonu"
                htmlFor="kg-sender-phone"
                hint="Kuryenin arayabileceği sabit veya GSM numarası"
                required={form.isEnabled}
              >
                <input
                  id="kg-sender-phone"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono text-sm"
                  value={form.senderPhone ?? ''}
                  onChange={(e) => setForm((c) => ({ ...c, senderPhone: e.target.value }))}
                  placeholder="Örn: 02161234567"
                />
              </Field>

              <div className="sm:col-span-2">
                <Field
                  label="Gönderici Depo Açık Adresi"
                  htmlFor="kg-sender-address"
                  hint="Kolay Gelsin kuryesinin paketleri teslim alacağı açık adres"
                  required={form.isEnabled}
                >
                  <input
                    id="kg-sender-address"
                    type="text"
                    disabled={!isEditable}
                    className="field text-sm"
                    value={form.senderAddress ?? ''}
                    onChange={(e) => setForm((c) => ({ ...c, senderAddress: e.target.value }))}
                    placeholder="Örn: Organize Sanayi Bölgesi 4. Cadde No:12"
                  />
                </Field>
              </div>

              <div>
                <Field label="Çıkış İli (Seçiniz)" htmlFor="kg-sender-city" required={form.isEnabled}>
                  <SearchableSelect
                    id="kg-sender-city"
                    disabled={!isEditable}
                    value={String(form.senderCityId || '')}
                    placeholder="İl Seçiniz..."
                    searchPlaceholder="İl adı veya plaka ara (örn: Konya veya 42)..."
                    options={(cities ?? []).map((c) => ({
                      value: String(c.id),
                      label: `${c.plate} - ${c.name}`,
                    }))}
                    onChange={(val) => {
                      const cId = Number(val);
                      const selected = cities?.find((c) => c.id === cId);
                      setForm((prev) => ({
                        ...prev,
                        senderCityId: cId,
                        senderCity: selected?.name ?? prev.senderCity,
                        senderDistrict: '',
                        senderDistrictId: cId * 1000 + 1,
                      }));
                    }}
                  />
                </Field>
              </div>

              <div>
                <Field label="Çıkış İlçesi (Seçiniz)" htmlFor="kg-sender-dist" required={form.isEnabled}>
                  <SearchableSelect
                    id="kg-sender-dist"
                    disabled={!isEditable || !form.senderCityId}
                    value={String(form.senderDistrictId || '')}
                    placeholder="İlçe Seçiniz..."
                    searchPlaceholder="İlçe ara (örn: Karatay, Selçuklu)..."
                    options={(districts ?? []).map((d) => ({
                      value: String(d.kolayGelsinDistrictId),
                      label: `${d.name} (Kod: ${d.kolayGelsinDistrictId})`,
                    }))}
                    onChange={(val) => {
                      const dId = Number(val);
                      const selected = districts?.find((d) => d.kolayGelsinDistrictId === dId);
                      setForm((prev) => ({
                        ...prev,
                        senderDistrictId: dId,
                        senderDistrict: selected?.name ?? prev.senderDistrict,
                      }));
                    }}
                  />
                </Field>
              </div>

              {depotNeighborhoods.length > 0 && (
                <div className="sm:col-span-2">
                  <Field label="Çıkış Depo Mahallesi (Hızlı Adres Ekle)" htmlFor="kg-sender-neighborhood">
                    <SearchableSelect
                      id="kg-sender-neighborhood"
                      disabled={!isEditable}
                      value=""
                      placeholder="Mahalle Seçiniz (Açık Adrese Eklemek İçin)..."
                      searchPlaceholder="Mahalle ara..."
                      isLoading={loadingNeighborhoods}
                      options={depotNeighborhoods.map((n) => ({
                        value: n.name,
                        label: n.fullName || `${n.name} Mah.`,
                      }))}
                      onChange={(selectedMahalle) => {
                        if (selectedMahalle) {
                          setForm((prev) => {
                            const cur = (prev.senderAddress ?? '').trim();
                            const toAdd = selectedMahalle.includes('Mah') ? selectedMahalle : `${selectedMahalle} Mah.`;
                            if (!cur.includes(toAdd)) {
                              return {
                                ...prev,
                                senderAddress: cur ? `${toAdd}, ${cur}` : toAdd,
                              };
                            }
                            return prev;
                          });
                          toast.success(`${selectedMahalle} depo açık adresine eklendi.`);
                        }
                      }}
                    />
                    <p className="mt-1 text-[11px] text-ink-500">
                      Seçilen mahalle otomatik olarak yukarıdaki &quot;Gönderici Depo Açık Adresi&quot; alanına eklenir.
                    </p>
                  </Field>
                </div>
              )}
            </div>
          </div>

          {/* Otomasyon ve İade Ayarları */}
          <div className="border-t border-ink-100 pt-5">
            <h3 className="text-sm font-semibold text-ink-900 mb-4">3. Otomasyon ve İade Ayarları</h3>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    disabled={!isEditable}
                    className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    checked={form.autoSyncEnabled}
                    onChange={(e) => setForm((c) => ({ ...c, autoSyncEnabled: e.target.checked }))}
                  />
                  <div>
                    <span className="text-sm font-medium text-ink-900">
                      Otomatik Kargo ve Teslimat Senkronizasyonu
                    </span>
                    <p className="text-xs text-ink-500 mt-0.5">
                      Kargodaki paketler periyodik taranarak "Dağıtımda" ve "Teslim Edildi" statüleri arka planda otomatik güncellenir.
                    </p>
                  </div>
                </label>
              </div>

              <Field
                label="Sorgulama Sıklığı"
                htmlFor="kg-interval"
                hint="Kolay Gelsin 30 dk önbellekleme kuralı gereğince 30 dk önerilir"
              >
                <select
                  id="kg-interval"
                  disabled={!isEditable || !form.autoSyncEnabled}
                  className="field text-sm"
                  value={form.syncIntervalMinutes}
                  onChange={(e) => setForm((c) => ({ ...c, syncIntervalMinutes: Number(e.target.value) }))}
                >
                  <option value={30}>Her 30 dakikada bir (Resmi Öneri)</option>
                  <option value={60}>Her 1 saatte bir</option>
                  <option value={120}>Her 2 saatte bir</option>
                  <option value={360}>Her 6 saatte bir</option>
                </select>
              </Field>

              <Field
                label="İade Takip Kodu Ön Eki"
                htmlFor="kg-ret-prefix"
                hint="Müşteriye verilecek Kolay Gelsin iade anlaşma kodunun başındaki harfler"
              >
                <input
                  id="kg-ret-prefix"
                  type="text"
                  disabled={!isEditable}
                  className="field font-mono text-sm uppercase"
                  value={form.returnCodePrefix}
                  onChange={(e) => setForm((c) => ({ ...c, returnCodePrefix: e.target.value.toUpperCase() }))}
                  placeholder="Örn: KG-IAD-"
                />
              </Field>
            </div>
          </div>

          {isEditable && (
            <div className="flex justify-end border-t border-ink-100 pt-5">
              <button
                type="submit"
                className="btn-primary"
                disabled={save.isPending}
              >
                {save.isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
                Ayarları Kaydet
              </button>
            </div>
          )}
        </form>
      </section>

      {/* 4. BARKODLU SİSTEM VE İŞ AKIŞI REHBERİ */}
      <div className="rounded-lg border border-brand-200 bg-brand-50/70 p-5 text-sm text-brand-900">
        <h3 className="font-semibold text-brand-950 flex items-center gap-2">
          <span>🏷️</span> Kolay Gelsin Barkodlu Sistem ve İşleyişi
        </h3>
        <ul className="mt-2.5 space-y-2 text-xs text-brand-800 list-disc list-inside">
          <li>
            <strong>Tek Tıkla Sevk Emri:</strong> Sipariş detay sayfasında <em>"Kolay Gelsin'e Gönder"</em> butonuna basıldığında, Kolay Gelsin REST API'sine anında sevk emri iletilir ve resmi kargo takip numarası tahsis edilir.
          </li>
          <li>
            <strong>Doğrudan Barkod Yazdırma:</strong> Sevk oluşturulduğu anda <em>"Barkod Yazdır"</em> butonu aktifleşir. Standart 100x150 mm termal etiket veya A4/A5 yazdırılabilir kargo fişi otomatik açılır ve termal etiket yazıcısına tek tıkla basılabilir.
          </li>
          <li>
            <strong>Kurye Barkod Okutma:</strong> Kolay Gelsin kuryesi depoya geldiğinde paket üzerindeki barkodu okuttuğu an kargo durumu otomatik olarak <em>"Kurye Teslim Aldı"</em> statüsüne geçer.
          </li>
          <li>
            <strong>Canlı Müşteri Takibi:</strong> Müşteri mağaza hesabında siparişini görüntülerken resmi Kolay Gelsin takip linkini ve adım adım kargo hareketlerini canlı görür.
          </li>
        </ul>
      </div>

      {/* Takip Detay Modalı */}
      {trackingModalData && (
        <Modal
          open={Boolean(trackingModalData)}
          title={`Kolay Gelsin Kargo Takip: ${trackingModalData.trackingNumber}`}
          description={trackingModalData.currentStatusText}
          onClose={() => setTrackingModalData(null)}
          size="lg"
          footer={
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setTrackingModalData(null)}
            >
              Kapat
            </button>
          }
        >
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-ink-50 p-4 border border-ink-100">
              <div>
                <p className="text-xs text-ink-500 font-medium">Anlık Kargo Durumu</p>
                <p className="text-base font-semibold text-ink-900 mt-0.5">
                  {trackingModalData.currentStatusText}
                </p>
              </div>
              {trackingModalData.deliveredTo && (
                <div className="text-right">
                  <p className="text-xs text-ink-500 font-medium">Teslim Alan</p>
                  <p className="text-sm font-semibold text-ink-900 mt-0.5">
                    {trackingModalData.deliveredTo}
                  </p>
                </div>
              )}
              {trackingModalData.deliveredAt && (
                <div className="text-right">
                  <p className="text-xs text-ink-500 font-medium">Teslim Tarihi</p>
                  <p className="text-sm font-medium text-ink-800 mt-0.5">
                    {formatDateTime(trackingModalData.deliveredAt)}
                  </p>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-3">
                Kargo Hareket Geçmişi
              </h4>
              {trackingModalData.events.length === 0 ? (
                <p className="text-sm text-ink-500 italic">Henüz bir kargo hareketi kaydedilmemiş.</p>
              ) : (
                <ol className="space-y-3 border-l-2 border-brand-300 pl-4">
                  {trackingModalData.events.map((ev, idx) => (
                    <li key={idx} className="relative text-sm">
                      <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-brand-600" />
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-ink-900">{ev.statusText}</span>
                        <span className="text-xs text-ink-400 font-mono">
                          {formatDateTime(ev.occurredAt)}
                        </span>
                      </div>
                      {ev.description && (
                        <p className="text-xs text-ink-600 mt-0.5">{ev.description}</p>
                      )}
                      {ev.location && (
                        <p className="text-xs text-ink-400 mt-0.5">📍 {ev.location}</p>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
/* =================================== VERGI =================================== */

const emptyTaxClass: TaxClassUpsertDto = {
  name: '',
  code: '',
  description: '',
  isDefault: false,
  isActive: true,
  rates: [{ name: 'KDV', rate: 20, countryCode: 'TR', isActive: true, appliesToShipping: true, priority: 0 }],
};

export function TaxPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{ id: number | null; form: TaxClassUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const classes = useQuery({
    queryKey: ['admin', 'tax-classes'],
    queryFn: () => adminTaxApi.classes(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'tax-classes'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminTaxApi.createClass(editing!.form)
        : adminTaxApi.updateClass(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Vergi sınıfı kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminTaxApi.deleteClass(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Vergi sınıfı silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Silinemedi.'),
  });

  if (classes.isLoading) return <PageLoader />;

  return (
    <div>
      <PageHeader
        title="Vergi"
        description="Ürün gruplarına göre vergi sınıfları ve oranları"
        actions={
          can(P.settings.manage) ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setEditing({ id: null, form: emptyTaxClass })}
            >
              Yeni vergi sınıfı
            </button>
          ) : null
        }
      />

      <div className="space-y-4">
        {(classes.data ?? []).map((taxClass) => (
          <section key={taxClass.id} className="card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">
                  {taxClass.name}
                  {taxClass.isDefault ? (
                    <span className="badge ml-2 bg-brand-50 text-brand-800">Varsayılan</span>
                  ) : null}
                  {!taxClass.isActive ? (
                    <span className="badge ml-2 bg-ink-100 text-ink-600">Pasif</span>
                  ) : null}
                </h3>
                <p className="font-mono text-xs text-ink-400">{taxClass.code}</p>
                {taxClass.description ? (
                  <p className="mt-1 text-sm text-ink-600">{taxClass.description}</p>
                ) : null}
              </div>

              {can(P.settings.manage) ? (
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="btn-ghost btn-sm"
                    onClick={() =>
                      setEditing({
                        id: taxClass.id,
                        form: {
                          name: taxClass.name,
                          code: taxClass.code,
                          description: taxClass.description,
                          isDefault: taxClass.isDefault,
                          isActive: taxClass.isActive,
                          rates: taxClass.rates.map((rate, index) => ({
                            id: rate.id,
                            name: rate.name,
                            rate: rate.rate,
                            countryCode: rate.countryCode,
                            province: rate.province,
                            isActive: rate.isActive,
                            appliesToShipping: rate.appliesToShipping,
                            priority: index,
                          })),
                        },
                      })
                    }
                  >
                    Düzenle
                  </button>
                  {!taxClass.isDefault ? (
                    <button
                      type="button"
                      className="btn-ghost btn-sm text-red-600"
                      onClick={() => setDeleteId(taxClass.id)}
                    >
                      Sil
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="scroll-x mt-4">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                    <th className="pb-2 font-semibold">Oran adı</th>
                    <th className="pb-2 text-right font-semibold">Oran</th>
                    <th className="pb-2 font-semibold">Ülke</th>
                    <th className="pb-2 text-center font-semibold">Kargoya uygulanır</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {taxClass.rates.map((rate) => (
                    <tr key={rate.id}>
                      <td className="py-2 text-ink-800">{rate.name}</td>
                      <td className="py-2 text-right font-medium">%{rate.rate}</td>
                      <td className="py-2 text-ink-600">
                        {rate.countryCode ?? 'Tümü'}
                        {rate.province ? ` / ${rate.province}` : ''}
                      </td>
                      <td className="py-2 text-center">{rate.appliesToShipping ? 'Evet' : 'Hayır'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}

        {(classes.data ?? []).length === 0 ? (
          <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
            Vergi sınıfı tanımlanmamış.
          </p>
        ) : null}
      </div>

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni vergi sınıfı' : 'Vergi sınıfını düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.name || !editing?.form.code}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ad" htmlFor="tx-name" required>
                <input
                  id="tx-name"
                  className="field"
                  value={editing.form.name}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, name: event.target.value } })
                  }
                />
              </Field>

              <Field label="Kod" htmlFor="tx-code" required>
                <input
                  id="tx-code"
                  className="field font-mono text-sm"
                  value={editing.form.code}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, code: event.target.value } })
                  }
                />
              </Field>
            </div>

            <Field label="Açıklama" htmlFor="tx-desc">
              <input
                id="tx-desc"
                className="field"
                value={editing.form.description ?? ''}
                onChange={(event) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, description: event.target.value } },
                  )
                }
              />
            </Field>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editing.form.isActive}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, isActive: event.target.checked } })
                  }
                />
                Aktif
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-brand-600"
                  checked={editing.form.isDefault}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, isDefault: event.target.checked } },
                    )
                  }
                />
                Varsayılan sınıf
              </label>
            </div>

            <div className="rounded-lg border border-ink-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-ink-900">Oranlar</h3>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            rates: [
                              ...c.form.rates,
                              {
                                name: '',
                                rate: 0,
                                countryCode: 'TR',
                                isActive: true,
                                appliesToShipping: false,
                                priority: c.form.rates.length,
                              },
                            ],
                          },
                        },
                    )
                  }
                >
                  Oran ekle
                </button>
              </div>

              <ul className="space-y-2">
                {editing.form.rates.map((rate, index) => (
                  <li key={index} className="grid gap-2 sm:grid-cols-[1fr_90px_90px_auto_auto]">
                    <input
                      className="field py-2 text-sm"
                      placeholder="KDV"
                      aria-label="Oran adı"
                      value={rate.name}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                rates: c.form.rates.map((item, i) =>
                                  i === index ? { ...item, name: event.target.value } : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      className="field py-2 text-sm"
                      aria-label="Oran"
                      value={rate.rate}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                rates: c.form.rates.map((item, i) =>
                                  i === index ? { ...item, rate: Number(event.target.value) } : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <input
                      className="field py-2 text-sm"
                      placeholder="TR"
                      maxLength={2}
                      aria-label="Ülke kodu"
                      value={rate.countryCode ?? ''}
                      onChange={(event) =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: {
                                ...c.form,
                                rates: c.form.rates.map((item, i) =>
                                  i === index
                                    ? { ...item, countryCode: event.target.value.toUpperCase() }
                                    : item,
                                ),
                              },
                            },
                        )
                      }
                    />
                    <label className="flex items-center gap-1.5 text-xs text-ink-600">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 rounded border-ink-300 text-brand-600"
                        checked={rate.appliesToShipping}
                        onChange={(event) =>
                          setEditing(
                            (c) =>
                              c && {
                                ...c,
                                form: {
                                  ...c.form,
                                  rates: c.form.rates.map((item, i) =>
                                    i === index
                                      ? { ...item, appliesToShipping: event.target.checked }
                                      : item,
                                  ),
                                },
                              },
                          )
                        }
                      />
                      Kargo
                    </label>
                    <button
                      type="button"
                      className="btn-ghost btn-sm text-red-600"
                      onClick={() =>
                        setEditing(
                          (c) =>
                            c && {
                              ...c,
                              form: { ...c.form, rates: c.form.rates.filter((_, i) => i !== index) },
                            },
                        )
                      }
                    >
                      Sil
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Vergi sınıfını sil"
        message="Bu sınıfa bağlı ürün varsa silme reddedilir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}
